import React, { useState, useRef, useCallback, useMemo } from 'react';
import { X, Check, Brain, Users, MapPin, Flame } from 'lucide-react';

interface BlockerData {
  blocker: string;
  context?: string;
  category?: 'internal' | 'external' | 'situational';
  severity?: number; // 0-10
  status?: string;
}

interface EnhancedBlockerCardProps {
  id: string;
  blocker: BlockerData;
  onFeedback: (id: string, content: string, feedbackType: 'helpful' | 'not_helpful') => void;
  onDismiss?: (id: string) => void;
  language?: string;
}

const TRANSLATIONS = {
  Italian: {
    keep: 'Conferma',
    dismiss: 'Rimuovi',
    removed: 'Rimosso',
    cancel: 'Annulla',
    internal: 'Interno',
    external: 'Esterno',
    situational: 'Situazionale',
    severity: 'Impatto',
    swipeHint: 'Scorri per feedback'
  },
  English: {
    keep: 'Confirm',
    dismiss: 'Remove',
    removed: 'Removed',
    cancel: 'Cancel',
    internal: 'Internal',
    external: 'External',
    situational: 'Situational',
    severity: 'Impact',
    swipeHint: 'Swipe for feedback'
  },
  Spanish: {
    keep: 'Confirmar',
    dismiss: 'Eliminar',
    removed: 'Eliminado',
    cancel: 'Cancelar',
    internal: 'Interno',
    external: 'Externo',
    situational: 'Situacional',
    severity: 'Impacto',
    swipeHint: 'Desliza para feedback'
  },
  German: {
    keep: 'Bestätigen',
    dismiss: 'Entfernen',
    removed: 'Entfernt',
    cancel: 'Abbrechen',
    internal: 'Intern',
    external: 'Extern',
    situational: 'Situativ',
    severity: 'Auswirkung',
    swipeHint: 'Wischen für Feedback'
  },
  French: {
    keep: 'Confirmer',
    dismiss: 'Supprimer',
    removed: 'Supprimé',
    cancel: 'Annuler',
    internal: 'Interne',
    external: 'Externe',
    situational: 'Situationnel',
    severity: 'Impact',
    swipeHint: 'Glissez pour feedback'
  }
};

// Category configuration with distinct visual identity
const CATEGORY_CONFIG: Record<string, {
  icon: typeof Brain;
  gradient: string;
  bg: string;
  border: string;
  text: string;
  subText: string;
  badge: string;
  severityColor: string;
}> = {
  internal: {
    icon: Brain,
    gradient: 'from-purple-500 to-indigo-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200/50',
    text: 'text-purple-900',
    subText: 'text-purple-700/70',
    badge: 'bg-purple-100 text-purple-700',
    severityColor: 'bg-purple-500'
  },
  external: {
    icon: Users,
    gradient: 'from-blue-500 to-cyan-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200/50',
    text: 'text-blue-900',
    subText: 'text-blue-700/70',
    badge: 'bg-blue-100 text-blue-700',
    severityColor: 'bg-blue-500'
  },
  situational: {
    icon: MapPin,
    gradient: 'from-amber-500 to-orange-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200/50',
    text: 'text-amber-900',
    subText: 'text-amber-700/70',
    badge: 'bg-amber-100 text-amber-700',
    severityColor: 'bg-amber-500'
  }
};

// Default fallback for unknown categories
const DEFAULT_CATEGORY_CONFIG = {
  icon: Flame,
  gradient: 'from-gray-500 to-slate-600',
  bg: 'bg-gray-50',
  border: 'border-gray-200/50',
  text: 'text-gray-900',
  subText: 'text-gray-700/70',
  badge: 'bg-gray-100 text-gray-700',
  severityColor: 'bg-gray-500'
};

const EnhancedBlockerCard: React.FC<EnhancedBlockerCardProps> = ({
  id,
  blocker,
  onFeedback,
  onDismiss,
  language = 'English'
}) => {
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<'helpful' | 'not_helpful' | null>(null);
  const startXRef = useRef(0);
  const velocityRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);

  const t = TRANSLATIONS[language as keyof typeof TRANSLATIONS] || TRANSLATIONS.English;
  const category = blocker.category || 'situational';
  const config = CATEGORY_CONFIG[category] || DEFAULT_CATEGORY_CONFIG;
  const CategoryIcon = config.icon;
  const severity = blocker.severity ?? 5;

  const SWIPE_THRESHOLD = 70;
  const VELOCITY_THRESHOLD = 0.5;

  // Haptic feedback
  const triggerHaptic = useCallback((intensity: number = 10) => {
    if (navigator.vibrate) {
      navigator.vibrate(intensity);
    }
  }, []);

  // Touch handlers with velocity tracking for smoother interaction
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isRemoving || feedbackGiven) return;
    startXRef.current = e.touches[0].clientX;
    lastXRef.current = e.touches[0].clientX;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;
    setIsDragging(true);
  }, [isRemoving, feedbackGiven]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || isRemoving || feedbackGiven) return;

    const currentX = e.touches[0].clientX;
    const currentTime = Date.now();
    const diff = currentX - startXRef.current;

    // Calculate velocity
    const timeDelta = currentTime - lastTimeRef.current;
    if (timeDelta > 0) {
      velocityRef.current = (currentX - lastXRef.current) / timeDelta;
    }
    lastXRef.current = currentX;
    lastTimeRef.current = currentTime;

    // If confirm is shown, allow right swipe to close
    if (showConfirm) {
      if (diff > 0) {
        const newTranslate = -100 + diff;
        setTranslateX(Math.min(newTranslate, 0));
      }
      return;
    }

    // Only allow left swipe
    if (diff < 0) {
      const resistance = Math.abs(diff) > SWIPE_THRESHOLD ? 0.3 : 1;
      const newTranslate = diff * resistance;
      setTranslateX(Math.max(newTranslate, -120));

      // Haptic at threshold
      if (Math.abs(diff) >= SWIPE_THRESHOLD && Math.abs(translateX) < SWIPE_THRESHOLD) {
        triggerHaptic(15);
      }
    }
  }, [isDragging, isRemoving, feedbackGiven, showConfirm, translateX, triggerHaptic]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);

    // Use velocity for more responsive feel
    const shouldTrigger = Math.abs(translateX) >= SWIPE_THRESHOLD ||
                          Math.abs(velocityRef.current) > VELOCITY_THRESHOLD;

    // If confirm was shown and user swiped right to close
    if (showConfirm && (translateX > -50 || velocityRef.current > VELOCITY_THRESHOLD)) {
      setTranslateX(0);
      setShowConfirm(false);
      return;
    }

    if (shouldTrigger && translateX < 0) {
      setTranslateX(-100);
      setShowConfirm(true);
      triggerHaptic(20);
    } else {
      setTranslateX(0);
      setShowConfirm(false);
    }
  }, [translateX, showConfirm, triggerHaptic]);

  const handleConfirmDismiss = useCallback(async () => {
    setIsRemoving(true);
    setFeedbackGiven('not_helpful');
    triggerHaptic(25);

    await onFeedback(id, blocker.blocker, 'not_helpful');

    setTimeout(() => {
      onDismiss?.(id);
    }, 300);
  }, [id, blocker.blocker, onFeedback, onDismiss, triggerHaptic]);

  const handleCancel = useCallback(() => {
    setTranslateX(0);
    setShowConfirm(false);
  }, []);

  const handleKeep = useCallback(() => {
    setFeedbackGiven('helpful');
    onFeedback(id, blocker.blocker, 'helpful');
    triggerHaptic(15);
    setTranslateX(0);
    setShowConfirm(false);
  }, [id, blocker.blocker, onFeedback, triggerHaptic]);

  // Severity bar segments
  const severitySegments = useMemo(() => {
    return Array.from({ length: 10 }, (_, i) => i < severity);
  }, [severity]);

  if (isRemoving) {
    return (
      <div className={`rounded-2xl overflow-hidden transition-all duration-300 ease-out opacity-0 max-h-0`}>
        <div className="p-4 text-center text-[12px] text-gray-400">{t.removed}</div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-2xl ${config.border} border shadow-sm`}>
      {/* Background action buttons */}
      <div className="absolute inset-0 flex items-stretch justify-end">
        <button
          onClick={handleKeep}
          className="w-16 bg-emerald-500 flex items-center justify-center transition-all duration-200"
          style={{ opacity: showConfirm ? 1 : 0, transform: showConfirm ? 'scale(1)' : 'scale(0.8)' }}
        >
          <Check size={22} className="text-white" />
        </button>
        <button
          onClick={handleConfirmDismiss}
          className="w-16 bg-red-500 flex items-center justify-center transition-all duration-200"
          style={{ opacity: Math.abs(translateX) > 20 ? 1 : 0 }}
        >
          <X size={22} className="text-white" />
        </button>
      </div>

      {/* Main card */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`${config.bg} relative z-10 p-4 transition-transform ${
          isDragging ? 'duration-0' : 'duration-200 ease-out'
        }`}
        style={{ transform: `translateX(${translateX}px)` }}
      >
        {/* Severity bar at top */}
        <div className="flex items-center gap-1.5 mb-3">
          <Flame size={10} className={`${config.subText} opacity-60`} />
          <div className="flex gap-0.5 flex-1">
            {severitySegments.map((active, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  active ? config.severityColor : 'bg-black/10'
                }`}
                style={{ opacity: active ? 0.4 + (i * 0.06) : 0.3 }}
              />
            ))}
          </div>
          <span className="text-[9px] font-bold text-black/30 ml-1">{severity}/10</span>
        </div>

        {/* Content */}
        <div className="flex items-start gap-3">
          {/* Category icon */}
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shrink-0 shadow-sm`}>
            <CategoryIcon size={16} className="text-white" />
          </div>

          <div className="flex-1 min-w-0">
            {/* Title */}
            <h4 className={`text-[13px] font-bold ${config.text} leading-snug mb-1`}>
              {blocker.blocker}
            </h4>

            {/* Context */}
            {blocker.context && (
              <p className={`text-[11px] ${config.subText} line-clamp-2 mb-2`}>
                {blocker.context}
              </p>
            )}

            {/* Category badge */}
            <div className="flex items-center gap-2">
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${config.badge}`}>
                {t[category]}
              </span>

              {/* Feedback indicator */}
              {feedbackGiven && (
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                  feedbackGiven === 'helpful' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                }`}>
                  {feedbackGiven === 'helpful' ? '✓' : '✕'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Swipe hint */}
        {!showConfirm && !feedbackGiven && translateX === 0 && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-25 pointer-events-none lg:hidden">
            <span className="text-[9px] font-medium">←</span>
            <div className="w-0.5 h-6 bg-black/30 rounded-full" />
          </div>
        )}

        {/* Close hint */}
        {showConfirm && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-black/40 font-medium pointer-events-none lg:hidden animate-pulse">
            → {t.cancel}
          </div>
        )}
      </div>

      {/* Desktop overlay */}
      {showConfirm && (
        <div className="hidden lg:flex absolute inset-0 z-20 items-center justify-end gap-2 pr-4 bg-gradient-to-l from-white via-white/95 to-transparent rounded-2xl">
          <button
            onClick={handleCancel}
            className="px-3 py-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-800 transition-colors"
          >
            {t.cancel}
          </button>
          <button
            onClick={handleKeep}
            className="px-3 py-1.5 text-[11px] font-bold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-all flex items-center gap-1 shadow-sm"
          >
            <Check size={12} />
            {t.keep}
          </button>
          <button
            onClick={handleConfirmDismiss}
            className="px-3 py-1.5 text-[11px] font-bold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-all flex items-center gap-1 shadow-sm"
          >
            <X size={12} />
            {t.dismiss}
          </button>
        </div>
      )}
    </div>
  );
};

export default EnhancedBlockerCard;
